import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  Download,
  Eye,
  RefreshCw,
  ArrowUpRight,
  ArrowDownLeft,
  DollarSign,
  Loader,
  AlertCircle
} from 'lucide-react';
import { transactionAPI } from '../utils/api';

const Transactions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalDocs: 0
  });

  // Fetch transactions on mount and when filters/page change
  useEffect(() => {
    fetchTransactions();
  }, [pagination.page, filterType, filterStatus]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchTransactions();
      } else if (searchTerm === '') {
        fetchTransactions();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort: '-createdAt'
      };

      // Add filters
      if (filterType !== 'all') {
        params.ramp_type = filterType;
      }
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await transactionAPI.getTransactions(params);

      if (response.data.success) {
        const data = response.data.data;
        setTransactions(data.docs || []);
        setPagination(prev => ({
          ...prev,
          totalPages: data.totalPages || 1,
          totalDocs: data.totalDocs || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      completed: 'badge-success',
      pending: 'badge-warning',
      processing: 'badge-info',
      failed: 'badge-error',
      cancelled: 'badge-error',
      expired: 'badge-error'
    };

    return (
      <span className={statusClasses[status] || 'badge-info'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) {
      return 'N/A';
    }
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Extract timestamp from MongoDB ObjectId
  const getTimestampFromId = (id) => {
    if (!id) return null;
    try {
      const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
      return new Date(timestamp);
    } catch (error) {
      return null;
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTransaction(null);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="vetme-heading-big text-vetme-dark">Transactions</h1>
          <p className="mt-2 text-vetme-gray-dark">
            View and manage all on-ramp and off-ramp transactions
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={fetchTransactions}
            className="btn-vetme-outline flex items-center"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button className="btn-vetme-primary flex items-center">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card-vetme p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-vetme-gray-dark w-5 h-5" />
              <input
                type="text"
                placeholder="Search by reference, user, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-vetme pl-10 w-full"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input-vetme w-full"
            >
              <option value="all">All Types</option>
              <option value="onramp">On-Ramp (Buy)</option>
              <option value="offramp">Off-Ramp (Sell)</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-vetme w-full"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Transactions Table */}
      <div className="card-vetme overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-vetme-gray-light">
            <thead className="bg-vetme-gray-lightest">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Crypto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-vetme-gray-light">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <Loader className="w-8 h-8 text-vetme-mint-dark animate-spin mx-auto" />
                    <p className="mt-2 text-vetme-gray-dark">Loading transactions...</p>
                  </td>
                </tr>
              ) : transactions.length > 0 ? (
                transactions.map((transaction) => (
                  <tr key={transaction.id || transaction._id} className="hover:bg-vetme-gray-lightest">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-vetme-dark">
                        {transaction.reference || 'N/A'}
                      </div>
                      <div className="text-sm text-vetme-gray-dark">
                        ID: {transaction.id?.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-vetme-dark">
                        {transaction.user?.first_name || 'User'} {transaction.user?.last_name || ''}
                      </div>
                      <div className="text-sm text-vetme-gray-dark">
                        {transaction.user?.phone || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {transaction.ramp_type === 'onramp' ? (
                        <div className="flex items-center text-green-600">
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          <span className="text-sm font-medium">On-Ramp</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-blue-600">
                          <ArrowDownLeft className="w-4 h-4 mr-1" />
                          <span className="text-sm font-medium">Off-Ramp</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-vetme-dark">
                        {formatCurrency(transaction.fiat_amount)}
                      </div>
                      <div className="text-xs text-vetme-gray-dark">
                        {transaction.fiat_currency || 'NGN'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-vetme-dark">
                        {transaction.final_crypto_amount?.toFixed(4) || '0.0000'}
                      </div>
                      <div className="text-xs text-vetme-gray-dark">
                        {transaction.crypto_currency || 'USDT'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(transaction.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-vetme-gray-dark">
                      {formatDate(
                        transaction.createdAt ||
                        getTimestampFromId(transaction.id || transaction._id)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(transaction)}
                        className="text-vetme-mint-dark hover:text-vetme-mint transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-vetme-gray-dark">
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-6 py-4 flex items-center justify-between border-t border-vetme-gray-light">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-vetme-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="btn-vetme-outline ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-vetme-gray-dark">
                  Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                  <span className="font-medium">{pagination.totalPages}</span> ({pagination.totalDocs} total transactions)
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="btn-vetme-outline rounded-l-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="btn-vetme-outline rounded-r-md ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details Modal */}
      {showModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-vetme-gray-light px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-vetme-dark">Transaction Details</h2>
                <p className="text-sm text-vetme-gray-dark mt-1">{selectedTransaction.reference}</p>
              </div>
              <button
                onClick={closeModal}
                className="text-vetme-gray-dark hover:text-vetme-dark transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center justify-center">
                {getStatusBadge(selectedTransaction.status)}
              </div>

              {/* Transaction Type */}
              <div className="text-center">
                {selectedTransaction.ramp_type === 'onramp' ? (
                  <div className="inline-flex items-center text-green-600 bg-green-50 px-4 py-2 rounded-full">
                    <ArrowUpRight className="w-5 h-5 mr-2" />
                    <span className="font-medium">On-Ramp Transaction (Buy Crypto)</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center text-blue-600 bg-blue-50 px-4 py-2 rounded-full">
                    <ArrowDownLeft className="w-5 h-5 mr-2" />
                    <span className="font-medium">Off-Ramp Transaction (Sell Crypto)</span>
                  </div>
                )}
              </div>

              {/* User Information */}
              <div className="bg-vetme-gray-lightest rounded-lg p-4">
                <h3 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">User Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-vetme-gray-dark">Name</p>
                    <p className="text-sm font-medium text-vetme-dark">
                      {selectedTransaction.user?.first_name} {selectedTransaction.user?.last_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-vetme-gray-dark">Phone</p>
                    <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.user?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-vetme-gray-dark">Email</p>
                    <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.user?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-vetme-gray-dark">User ID</p>
                    <p className="text-sm font-medium text-vetme-dark font-mono">{selectedTransaction.user?.id?.substring(0, 12)}...</p>
                  </div>
                </div>
              </div>

              {/* Transaction Amounts */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-xs text-green-700 uppercase font-medium mb-1">Fiat Amount</p>
                  <p className="text-2xl font-bold text-green-800">{formatCurrency(selectedTransaction.fiat_amount)}</p>
                  <p className="text-xs text-green-600 mt-1">{selectedTransaction.fiat_currency || 'NGN'}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-xs text-blue-700 uppercase font-medium mb-1">Crypto Amount</p>
                  <p className="text-2xl font-bold text-blue-800">{selectedTransaction.final_crypto_amount?.toFixed(4) || '0.0000'}</p>
                  <p className="text-xs text-blue-600 mt-1">{selectedTransaction.crypto_currency || 'USDT'}</p>
                </div>
              </div>

              {/* Transaction Details */}
              <div className="bg-vetme-gray-lightest rounded-lg p-4">
                <h3 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">Transaction Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-vetme-gray-dark">Reference</span>
                    <span className="text-sm font-medium text-vetme-dark font-mono">{selectedTransaction.reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-vetme-gray-dark">Network</span>
                    <span className="text-sm font-medium text-vetme-dark uppercase">{selectedTransaction.network || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-vetme-gray-dark">Exchange Rate</span>
                    <span className="text-sm font-medium text-vetme-dark">₦{selectedTransaction.exchange_rate?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-vetme-gray-dark">Processor Fee</span>
                    <span className="text-sm font-medium text-vetme-dark">{formatCurrency(selectedTransaction.processor_fee || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-vetme-gray-dark">Total Fee</span>
                    <span className="text-sm font-medium text-vetme-dark">{formatCurrency(selectedTransaction.total_fee || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-vetme-gray-dark">Commission Rate</span>
                    <span className="text-sm font-medium text-vetme-dark">{selectedTransaction.commission_rate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-vetme-gray-dark">Internal Transfer Status</span>
                    <span className="text-sm font-medium text-vetme-dark capitalize">{selectedTransaction.internal_transfer_status || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details - Conditional based on transaction type */}
              {selectedTransaction.ramp_type === 'onramp' ? (
                /* On-Ramp: Show Wallet Addresses */
                <div className="bg-vetme-gray-lightest rounded-lg p-4">
                  <h3 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">Wallet Information</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-vetme-gray-dark mb-1">User Wallet Address (Destination)</p>
                      <p className="text-sm font-medium text-vetme-dark font-mono break-all">{selectedTransaction.user_wallet_address || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-vetme-gray-dark mb-1">VetmePay Wallet Address (Source)</p>
                      <p className="text-sm font-medium text-vetme-dark font-mono break-all">{selectedTransaction.vetmepay_wallet_address || 'N/A'}</p>
                    </div>
                    {selectedTransaction.internal_transfer_hash && (
                      <div>
                        <p className="text-xs text-vetme-gray-dark mb-1">Transfer Hash</p>
                        <p className="text-sm font-medium text-vetme-dark font-mono break-all">{selectedTransaction.internal_transfer_hash}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Off-Ramp: Show Bank Details */
                selectedTransaction.user_bank_details ? (
                  <div className="bg-vetme-gray-lightest rounded-lg p-4">
                    <h3 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">User Bank Details (Payout Destination)</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-vetme-gray-dark">Bank Name</p>
                        <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.user_bank_details.bank_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-vetme-gray-dark">Account Number</p>
                        <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.user_bank_details.account_number}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-vetme-gray-dark">Account Name</p>
                        <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.user_bank_details.account_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-vetme-gray-dark">Bank Code</p>
                        <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.user_bank_details.bank_code}</p>
                      </div>
                    </div>
                    {selectedTransaction.quidax_deposit_address && (
                      <div className="mt-3 pt-3 border-t border-vetme-gray-light">
                        <p className="text-xs text-vetme-gray-dark mb-1">Crypto Deposit Address (Where user sent crypto)</p>
                        <p className="text-sm font-medium text-vetme-dark font-mono break-all">{selectedTransaction.quidax_deposit_address}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <p className="text-sm text-yellow-800">User bank details not available for this off-ramp transaction.</p>
                  </div>
                )
              )}

              {/* VetmePay Bank Account (For On-Ramp only - where user sends money) */}
              {selectedTransaction.ramp_type === 'onramp' && selectedTransaction.bank_details && (
                <div className="bg-vetme-gray-lightest rounded-lg p-4">
                  <h3 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">VetmePay Bank Account (Payment Destination)</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-vetme-gray-dark">Bank Name</p>
                      <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.bank_details.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-vetme-gray-dark">Account Number</p>
                      <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.bank_details.account_number}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-vetme-gray-dark">Account Name</p>
                      <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.bank_details.account_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-vetme-gray-dark">Bank Code</p>
                      <p className="text-sm font-medium text-vetme-dark">{selectedTransaction.bank_details.bank_code}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="bg-vetme-gray-lightest rounded-lg p-4">
                <h3 className="text-sm font-medium text-vetme-gray-dark uppercase mb-3">Timeline</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-vetme-gray-dark">Created</span>
                    <span className="text-sm font-medium text-vetme-dark">
                      {formatDate(selectedTransaction.createdAt || getTimestampFromId(selectedTransaction.id))}
                    </span>
                  </div>
                  {selectedTransaction.completed_at && (
                    <div className="flex justify-between">
                      <span className="text-sm text-vetme-gray-dark">Completed</span>
                      <span className="text-sm font-medium text-vetme-dark">{formatDate(selectedTransaction.completed_at)}</span>
                    </div>
                  )}
                  {selectedTransaction.failed_at && (
                    <div className="flex justify-between">
                      <span className="text-sm text-vetme-gray-dark">Failed</span>
                      <span className="text-sm font-medium text-red-600">{formatDate(selectedTransaction.failed_at)}</span>
                    </div>
                  )}
                  {selectedTransaction.expires_at && (
                    <div className="flex justify-between">
                      <span className="text-sm text-vetme-gray-dark">Expires</span>
                      <span className="text-sm font-medium text-vetme-dark">{formatDate(selectedTransaction.expires_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-vetme-gray-light px-6 py-4 flex justify-end space-x-3">
              <button onClick={closeModal} className="btn-vetme-outline">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
